import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Clock, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Badge } from '@/components/Badge';
import { slaService } from '@/services/slaService';
import type { 
  SLAPolicy, 
  CreateSLAPolicyRequest, 
  UpdateSLAPolicyRequest, 
  SLAPolicyFormData,
  BusinessHoursCalendar
} from '@/types/sla';
import { SLATier } from '@/types/sla';

const parseTimeSpan = (timeSpan: string): { hours: number; minutes: number } => {
  const parts = timeSpan.split(':');
  return { hours: parseInt(parts[0] || '9', 10), minutes: parseInt(parts[1] || '0', 10) };
};

interface SLAPolicyFormProps {
  initialData?: SLAPolicy;
  onSubmit: (data: CreateSLAPolicyRequest | UpdateSLAPolicyRequest) => Promise<void>;
  onCancel: () => void;
}

/**
 * SLA Policy Form Component
 * Comprehensive form for creating and editing SLA policies
 */
export const SLAPolicyForm: React.FC<SLAPolicyFormProps> = ({
  initialData,
  onSubmit,
  onCancel
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [businessHoursCalendars, setBusinessHoursCalendars] = useState<BusinessHoursCalendar[]>([]);
  const [activeTab, setActiveTab] = useState<'basic' | 'response-times' | 'business-hours' | 'advanced'>('basic');

  const { register, handleSubmit, formState: { errors }, watch, setValue, getValues } = useForm<SLAPolicyFormData>({
    defaultValues: initialData ? {
      name: initialData.name,
      description: initialData.description || '',
      firstResponseLowPriorityMinutes: initialData.firstResponseLowPriorityMinutes,
      firstResponseMediumPriorityMinutes: initialData.firstResponseMediumPriorityMinutes,
      firstResponseHighPriorityMinutes: initialData.firstResponseHighPriorityMinutes,
      firstResponseUrgentPriorityMinutes: initialData.firstResponseUrgentPriorityMinutes,
      firstResponseCriticalPriorityMinutes: initialData.firstResponseCriticalPriorityMinutes,
      resolutionLowPriorityMinutes: initialData.resolutionLowPriorityMinutes,
      resolutionMediumPriorityMinutes: initialData.resolutionMediumPriorityMinutes,
      resolutionHighPriorityMinutes: initialData.resolutionHighPriorityMinutes,
      resolutionUrgentPriorityMinutes: initialData.resolutionUrgentPriorityMinutes,
      resolutionCriticalPriorityMinutes: initialData.resolutionCriticalPriorityMinutes,
      businessHoursStart: parseTimeSpan(initialData.businessHoursStart),
      businessHoursEnd: parseTimeSpan(initialData.businessHoursEnd),
      businessDays: initialData.businessDays,
      timeZone: initialData.timeZone,
      excludeWeekends: initialData.excludeWeekends,
      excludeHolidays: initialData.excludeHolidays,
      businessHoursOnly: initialData.businessHoursOnly,
      businessHoursCalendarId: initialData.businessHoursCalendarId || '',
      tier: initialData.tier,
      autoPauseOnCustomerWait: initialData.autoPauseOnCustomerWait,
      enablePredictiveSLA: initialData.enablePredictiveSLA,
      isActive: initialData.isActive,
      isDefault: initialData.isDefault
    } : {
      name: '',
      description: '',
      firstResponseLowPriorityMinutes: 1440, // 24 hours
      firstResponseMediumPriorityMinutes: 480, // 8 hours
      firstResponseHighPriorityMinutes: 240, // 4 hours
      firstResponseUrgentPriorityMinutes: 60, // 1 hour
      firstResponseCriticalPriorityMinutes: 30, // 30 minutes
      resolutionLowPriorityMinutes: 10080, // 7 days
      resolutionMediumPriorityMinutes: 4320, // 3 days
      resolutionHighPriorityMinutes: 1440, // 24 hours
      resolutionUrgentPriorityMinutes: 480, // 8 hours
      resolutionCriticalPriorityMinutes: 240, // 4 hours
      businessHoursStart: { hours: 9, minutes: 0 },
      businessHoursEnd: { hours: 17, minutes: 0 },
      businessDays: 'Monday,Tuesday,Wednesday,Thursday,Friday',
      timeZone: 'UTC',
      excludeWeekends: true,
      excludeHolidays: true,
      businessHoursOnly: true,
      businessHoursCalendarId: '',
      tier: SLATier.Basic,
      autoPauseOnCustomerWait: true,
      enablePredictiveSLA: false,
      isActive: true,
      isDefault: false
    }
  });

  useEffect(() => {
    loadBusinessHoursCalendars();
  }, []);

  const loadBusinessHoursCalendars = async () => {
    try {
      const calendars = await slaService.getBusinessHoursCalendars();
      setBusinessHoursCalendars(calendars);
    } catch (err) {
      console.error('Error loading business hours calendars:', err);
    }
  };

  const formatTimeSpan = (time: { hours: number; minutes: number }): string => {
    return `${time.hours.toString().padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}:00`;
  };

  const formatMinutes = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    return `${days}d ${hours}h`;
  };

  const validateResponseTimes = (): string | null => {
    const values = getValues();
    
    console.log('🔍 Validating response times:', {
      firstResponse: {
        critical: values.firstResponseCriticalPriorityMinutes,
        urgent: values.firstResponseUrgentPriorityMinutes,
        high: values.firstResponseHighPriorityMinutes,
        medium: values.firstResponseMediumPriorityMinutes,
        low: values.firstResponseLowPriorityMinutes
      },
      resolution: {
        critical: values.resolutionCriticalPriorityMinutes,
        urgent: values.resolutionUrgentPriorityMinutes,
        high: values.resolutionHighPriorityMinutes,
        medium: values.resolutionMediumPriorityMinutes,
        low: values.resolutionLowPriorityMinutes
      }
    });

    // Check for required values first
    if (!values.firstResponseCriticalPriorityMinutes || !values.resolutionCriticalPriorityMinutes) {
      console.log('⚠️ Missing required response time values');
      return 'All response time values are required';
    }

    // Very lenient validation: Just check for reasonable minimum values
    if (values.firstResponseCriticalPriorityMinutes < 1 || values.resolutionCriticalPriorityMinutes < 1) {
      return 'Response times must be at least 1 minute';
    }

    // Check for maximum reasonable values to prevent issues
    if (values.firstResponseCriticalPriorityMinutes > 525600 || values.resolutionCriticalPriorityMinutes > 525600) {
      return 'Response times cannot exceed 1 year (525600 minutes)';
    }

    console.log('✅ Response times validation passed');
    return null;
  };

  const onFormSubmit = async (formData: SLAPolicyFormData) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔄 SLA Policy Form Submission Started', {
        formData: {
          name: formData.name,
          tier: formData.tier,
          firstResponseTimes: {
            critical: formData.firstResponseCriticalPriorityMinutes,
            urgent: formData.firstResponseUrgentPriorityMinutes,
            high: formData.firstResponseHighPriorityMinutes,
            medium: formData.firstResponseMediumPriorityMinutes,
            low: formData.firstResponseLowPriorityMinutes
          }
        }
      });

      // Validate response times
      const validationError = validateResponseTimes();
      if (validationError) {
        console.error('❌ SLA Policy Validation Failed:', validationError);
        setError(validationError);
        return;
      }

      if (formData.businessHoursOnly && !formData.businessHoursCalendarId && (!initialData || initialData.businessHoursCalendarId)) {
        setError('Create a business hours calendar and select it before saving this policy.');
        setActiveTab('business-hours');
        return;
      }

      // Convert form data to API format
      const apiData: CreateSLAPolicyRequest | UpdateSLAPolicyRequest = {
        ...formData,
        businessHoursStart: formatTimeSpan(formData.businessHoursStart),
        businessHoursEnd: formatTimeSpan(formData.businessHoursEnd),
        businessHoursCalendarId: formData.businessHoursCalendarId || undefined
      };

      console.log('✅ SLA Policy API Data Prepared', {
        name: apiData.name,
        businessHours: `${apiData.businessHoursStart} - ${apiData.businessHoursEnd}`,
        tier: apiData.tier
      });

      await onSubmit(apiData);
      console.log('🎉 SLA Policy Submission Successful');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save SLA policy';
      console.error('❌ SLA Policy Submission Failed:', errorMessage);
      setError(errorMessage);
      throw err; // Re-throw to ensure calling code knows about failure
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', name: 'Basic Info', icon: Info },
    { id: 'response-times', name: 'Response Times', icon: Clock },
    { id: 'business-hours', name: 'Business Hours', icon: Clock },
    { id: 'advanced', name: 'Advanced', icon: AlertCircle }
  ];

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Basic Info Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Input
                    label="Policy Name"
                    {...register('name', { required: 'Policy name is required' })}
                    error={errors.name?.message}
                    placeholder="e.g., Standard Support SLA"
                    data-testid="sla-policy-name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tier
                  </label>
                  <select
                    {...register('tier', { 
                      required: 'SLA tier is required',
                      setValueAs: (value) => {
                        const numValue = parseInt(value, 10);
                        console.log('🔄 Converting tier value:', { original: value, converted: numValue });
                        return numValue;
                      }
                    })}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    data-testid="sla-policy-tier"
                  >
                    <option value={SLATier.Basic}>Basic</option>
                    <option value={SLATier.Premium}>Premium</option>
                    <option value={SLATier.Enterprise}>Enterprise</option>
                    <option value={SLATier.VIP}>VIP</option>
                    <option value={SLATier.Custom}>Custom</option>
                  </select>
                  {errors.tier && (
                    <p className="mt-1 text-sm text-red-600">{errors.tier.message}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    {...register('description')}
                    placeholder="Describe this SLA policy and when it should be used"
                    data-testid="sla-policy-description"
                    rows={3}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  />
                </div>

                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('isActive')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Active</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('isDefault')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Default Policy</span>
                  </label>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Response Times Tab */}
        {activeTab === 'response-times' && (
          <div className="space-y-6">
            {/* First Response Times */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">First Response Times</h3>
              <p className="text-sm text-gray-600 mb-6">
                Define how quickly agents should respond to new tickets based on priority.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { key: 'firstResponseCriticalPriorityMinutes', label: 'Critical', color: 'red' },
                  { key: 'firstResponseUrgentPriorityMinutes', label: 'Urgent', color: 'orange' },
                  { key: 'firstResponseHighPriorityMinutes', label: 'High', color: 'yellow' },
                  { key: 'firstResponseMediumPriorityMinutes', label: 'Medium', color: 'blue' },
                  { key: 'firstResponseLowPriorityMinutes', label: 'Low', color: 'green' }
                ].map(({ key, label, color }) => {
                  const value = watch(key as keyof SLAPolicyFormData) as number;
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">
                          {label} Priority
                        </label>
                        <Badge className={`bg-${color}-100 text-${color}-800`}>
                          {formatMinutes(value || 0)}
                        </Badge>
                      </div>
                      <Input
                        type="number"
                        {...register(key as keyof SLAPolicyFormData, { 
                          required: 'Target time is required',
                          min: { value: 1, message: 'Target time must be at least 1 minute' }
                        })}
                        error={errors[key as keyof SLAPolicyFormData]?.message}
                        placeholder="Minutes"
                        data-testid={`sla-first-response-${label.toLowerCase()}`}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Resolution Times */}
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Resolution Times</h3>
              <p className="text-sm text-gray-600 mb-6">
                Define how quickly tickets should be resolved based on priority.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { key: 'resolutionCriticalPriorityMinutes', label: 'Critical', color: 'red' },
                  { key: 'resolutionUrgentPriorityMinutes', label: 'Urgent', color: 'orange' },
                  { key: 'resolutionHighPriorityMinutes', label: 'High', color: 'yellow' },
                  { key: 'resolutionMediumPriorityMinutes', label: 'Medium', color: 'blue' },
                  { key: 'resolutionLowPriorityMinutes', label: 'Low', color: 'green' }
                ].map(({ key, label, color }) => {
                  const value = watch(key as keyof SLAPolicyFormData) as number;
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-sm font-medium text-gray-700">
                          {label} Priority
                        </label>
                        <Badge className={`bg-${color}-100 text-${color}-800`}>
                          {formatMinutes(value || 0)}
                        </Badge>
                      </div>
                      <Input
                        type="number"
                        {...register(key as keyof SLAPolicyFormData, { 
                          required: 'Target time is required',
                          min: { value: 1, message: 'Target time must be at least 1 minute' }
                        })}
                        error={errors[key as keyof SLAPolicyFormData]?.message}
                        placeholder="Minutes"
                        data-testid={`sla-resolution-${label.toLowerCase()}`}
                      />
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* Business Hours Tab */}
        {activeTab === 'business-hours' && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Hours Configuration</h3>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('businessHoursOnly')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Business hours only</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('excludeWeekends')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Exclude weekends</span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('excludeHolidays')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Exclude holidays</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Hours Start
                    </label>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        {...register('businessHoursStart.hours')}
                        placeholder="Hours"
                      />
                      <span className="text-gray-500">:</span>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        {...register('businessHoursStart.minutes')}
                        placeholder="Minutes"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Business Hours End
                    </label>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        min="0"
                        max="23"
                        {...register('businessHoursEnd.hours')}
                        placeholder="Hours"
                      />
                      <span className="text-gray-500">:</span>
                      <Input
                        type="number"
                        min="0"
                        max="59"
                        {...register('businessHoursEnd.minutes')}
                        placeholder="Minutes"
                      />
                    </div>
                  </div>

                  <div>
                    <Input
                      label="Time Zone"
                      {...register('timeZone')}
                      placeholder="UTC"
                    />
                  </div>
                </div>

                <div>
                  <Input
                    label="Business Days"
                    {...register('businessDays')}
                    placeholder="Monday,Tuesday,Wednesday,Thursday,Friday"
                    helperText="Comma-separated list of business days"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Business Hours Calendar
                  </label>
                  <select
                    {...register('businessHoursCalendarId')}
                    className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                  >
                    <option value="">{initialData && !initialData.businessHoursCalendarId ? 'Use existing policy schedule' : 'Select a calendar'}</option>
                    {businessHoursCalendars.map((calendar) => (
                      <option key={calendar.id} value={calendar.id}>
                        {calendar.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Advanced Tab */}
        {activeTab === 'advanced' && (
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced Settings</h3>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      {...register('autoPauseOnCustomerWait')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Auto-pause when waiting for customer</span>
                  </label>

                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <Info className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-1">Advanced Features</p>
                      <ul className="list-disc list-inside space-y-1">
                        <li>Auto-pause prevents SLA breaches when waiting for customer response</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end space-x-3 pt-6 border-t">
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          data-testid={initialData ? "update-sla-policy-button" : "create-sla-policy-button"}
        >
          {initialData ? 'Update Policy' : 'Create Policy'}
        </Button>
      </div>
    </form>
  );
};
